import { z } from 'zod';
import { Ulid } from './common.js';
/**
 * Update from the game (`docs/update-button-plan.md`): a web game's update card asks whether the
 * desktop app that paired *this* Board can install the newer build, and installs it on a press.
 *
 * Three parties read this module, and none of them may drift from it:
 *
 *  - the injected web layer, which asks, logs the code, presses and watches (its constants are baked
 *    in by `patcher/dump-constants.mjs`);
 *  - the Worker, which rings the candidate desktop apps and turns a press into an ordinary job
 *    (`apps/api/src/routes/layer-update.ts`);
 *  - the desktop app, which reads its rings and looks for the code in the game's live log on its own
 *    Board (`apps/desktop/core/src/rings.rs`).
 *
 * **The proof is a code, not an id** (plan §4). The game can read nothing that names its Board, so
 * the layer writes a fresh random code to its console, and the desktop app that finds that code in
 * the game's own log tag on a Board it paired is the one that answers. Board Browser sets the tag,
 * not the page, so a game cannot write under another game's.
 */
/** How long the card waits for an answer before it is drawn without Update. The owner's number. */
export const UPDATE_ASK_SECONDS = 30;
/**
 * How long the Worker holds an ask before answering *no*. Short of the layer's own limit, so that
 * an answer always arrives before the layer stops listening, with room for the round trip.
 */
export const UPDATE_ASK_SERVER_MS = 27_000;
/** How often the layer writes its line while it waits. A running game's log keeps about 12 s. */
export const UPDATE_CHECK_LOG_SECONDS = 3;
/** The line the layer writes, then the code. ASCII, as everything the layer writes must be. */
export const UPDATE_CHECK_LINE = '[BoardFlight] update-check ';
/** 128 random bits as 32 lowercase hex characters. */
export const UpdateCode = z.string().regex(/^[0-9a-f]{32}$/);
/** How long a ticket can be pressed with. A card can stay up while the game is played. */
export const UPDATE_TICKET_MS = 30 * 60 * 1000;
/**
 * The card's clocks once Update is pressed (plan §7.3). It reads the install twice a second, so the
 * old page runs against the new files for about a second before it reloads.
 */
export const UPDATE_STATUS_POLL_MS = 500;
/** No `installing` this long after the press: the desktop app never took it. */
export const UPDATE_START_TIMEOUT_MS = 30_000;
/** No `installed` this long after the press. */
export const UPDATE_INSTALL_TIMEOUT_MS = 5 * 60 * 1000;
/** `installed`, but the layer's own file still names the old build after this long. */
export const UPDATE_FILE_CONFIRM_MS = 10_000;
/**
 * More candidate accounts than this are not rung, and the answer is *no*. One account per Board is
 * the usual case; the cap only bounds what one public ingest key can make the Worker do.
 */
export const UPDATE_ASK_MAX_ACCOUNTS = 25;
/** The layer's identification, the same two values the config read sends. */
const LayerIdentity = {
    key: z.string().min(1).max(128),
    build: z.string().max(64),
};
/** A sealed ticket. Opaque to the page; only this Worker can open one. */
export const UpdateTicket = z.string().min(16).max(2048);
/** `POST /v1/feedback-layer/update-ask` */
export const UpdateAskRequest = z.object({ ...LayerIdentity, code: UpdateCode });
export const UpdateAskResponse = z.union([
    z.object({ canUpdate: z.literal(false) }),
    z.object({ canUpdate: z.literal(true), ticket: UpdateTicket }),
]);
/** `POST /v1/feedback-layer/update` */
export const UpdatePressRequest = z.object({ ...LayerIdentity, ticket: UpdateTicket });
export const UpdatePressResponse = z.object({ job: Ulid });
/** `POST /v1/feedback-layer/update-status` */
export const UpdateStatusRequest = z.object({ ...LayerIdentity, ticket: UpdateTicket, job: Ulid });
/**
 * What the card may know about an install: four words and a fraction. Never Board's message, the
 * Board's name or the account's — the page is the developer's code, and a Board's name belongs to
 * the account that connected it (`CLAUDE.md`).
 */
export const UpdateState = z.enum(['waiting', 'installing', 'installed', 'failed']);
export const UpdateStatusResponse = z.object({
    state: UpdateState,
    progress: z.number().min(0).max(1).nullable(),
});
/** A job's eight statuses as the card's four. */
export function updateStateOf(status) {
    switch (status) {
        case 'queued':
        case 'claimed':
            return 'waiting';
        case 'downloading':
        case 'installing':
            return 'installing';
        case 'installed':
            return 'installed';
        default:
            return 'failed';
    }
}
/**
 * What a ring asks the desktop app to look for: an update card's line, or a feedback sheet's
 * (`feedback-ask.ts`). The server names the kind and never a line — each kind's line is fixed in
 * the app — so BoardFlight cannot make a desktop app look for anything else in a Board's log.
 */
export const RingPurpose = z.enum(['update', 'feedback']);
/**
 * The query `GET /v1/desktop/update-rings` takes to list feedback rings too: `?purposes=update,feedback`.
 * A desktop app that does not send it is never sent a feedback ring, and is told no ring's purpose.
 */
export const RING_PURPOSES_PARAM = 'purposes';
/**
 * `GET /v1/desktop/update-rings` — one ask this desktop app was rung for.
 *
 * `packageName` is the game's package id, which is how the app finds the game's id on the Board
 * (`GET /v1/apps`), and so its log tag. `boards` are the candidate Boards this desktop has reached.
 * `purpose` is present only for a desktop app that asked with `?purposes=`.
 */
export const UpdateRing = z.object({
    ask: Ulid,
    purpose: RingPurpose.optional(),
    appId: Ulid,
    packageName: z.string(),
    code: UpdateCode,
    boards: z.array(Ulid),
    /** Epoch ms after which an answer is not heard. */
    until: z.number().int(),
});
export const UpdateRingsResponse = z.object({ items: z.array(UpdateRing) });
/** `POST /v1/desktop/update-rings/:ask` — the code was seen in the game's log on this Board. */
export const UpdateRingAnswer = z.object({ board: Ulid });
//# sourceMappingURL=update-ask.js.map